/**
 * API client for organization settings (Company Info, Bank Accounts, Signatures, Cheque Settings)
 */

import type {
  ApiResponse,
  BankAccountPublic,
  ChequeSettingsPublic,
  CompanyInfoPublic,
  CreateBankAccountRequest,
  CreateChequeSettingsRequest,
  SignaturePublic,
  UpdateBankAccountRequest,
  UpdateChequeSettingsRequest,
  UpdateCompanyInfoRequest,
  UpdateSignatureRequest,
} from '@garden-platform/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API Error: ${response.status}`);
  }

  return response.json();
}

// =============================================================================
// Company Info API
// =============================================================================

export const companyInfoApi = {
  get: async (): Promise<ApiResponse<CompanyInfoPublic>> => {
    return request<CompanyInfoPublic>('/organizations/company-info');
  },

  update: async (data: UpdateCompanyInfoRequest): Promise<ApiResponse<CompanyInfoPublic>> => {
    return request<CompanyInfoPublic>('/organizations/company-info', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  uploadLogo: async (file: File): Promise<ApiResponse<{ logoPath: string; hasLogo: boolean }>> => {
    const formData = new FormData();
    formData.append('logo', file);

    return request<{ logoPath: string; hasLogo: boolean }>('/organizations/logo', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  },

  deleteLogo: async (): Promise<ApiResponse<{ hasLogo: boolean }>> => {
    return request<{ hasLogo: boolean }>('/organizations/logo', {
      method: 'DELETE',
    });
  },

  getLogoUrl: (): string => {
    return `${API_BASE_URL}/organizations/logo`;
  },
};

// =============================================================================
// Bank Accounts API
// =============================================================================

export const bankAccountsApi = {
  list: async (includeInactive = false): Promise<ApiResponse<BankAccountPublic[]>> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return request<BankAccountPublic[]>(`/organizations/bank-accounts${query}`);
  },

  get: async (id: string): Promise<ApiResponse<BankAccountPublic>> => {
    return request<BankAccountPublic>(`/organizations/bank-accounts/${id}`);
  },

  create: async (data: CreateBankAccountRequest): Promise<ApiResponse<BankAccountPublic>> => {
    return request<BankAccountPublic>('/organizations/bank-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (
    id: string,
    data: UpdateBankAccountRequest,
  ): Promise<ApiResponse<BankAccountPublic>> => {
    return request<BankAccountPublic>(`/organizations/bank-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  setDefault: async (id: string): Promise<ApiResponse<BankAccountPublic>> => {
    return request<BankAccountPublic>(`/organizations/bank-accounts/${id}/default`, {
      method: 'PUT',
    });
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    return request<null>(`/organizations/bank-accounts/${id}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// Signatures API
// =============================================================================

export const signaturesApi = {
  list: async (includeInactive = false): Promise<ApiResponse<SignaturePublic[]>> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return request<SignaturePublic[]>(`/organizations/signatures${query}`);
  },

  get: async (id: string): Promise<ApiResponse<SignaturePublic>> => {
    return request<SignaturePublic>(`/organizations/signatures/${id}`);
  },

  upload: async (
    file: File,
    name: string,
    title?: string,
  ): Promise<ApiResponse<SignaturePublic>> => {
    const formData = new FormData();
    formData.append('signature', file);
    formData.append('name', name);
    if (title) {
      formData.append('title', title);
    }

    return request<SignaturePublic>('/organizations/signatures', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  },

  update: async (
    id: string,
    data: UpdateSignatureRequest,
  ): Promise<ApiResponse<SignaturePublic>> => {
    return request<SignaturePublic>(`/organizations/signatures/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    return request<null>(`/organizations/signatures/${id}`, {
      method: 'DELETE',
    });
  },

  getImageUrl: (id: string): string => {
    return `${API_BASE_URL}/organizations/signatures/${id}/download`;
  },
};

// =============================================================================
// Cheque Settings API
// =============================================================================

export const chequeSettingsApi = {
  list: async (includeInactive = false): Promise<ApiResponse<ChequeSettingsPublic[]>> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return request<ChequeSettingsPublic[]>(`/organizations/cheque-settings${query}`);
  },

  get: async (id: string): Promise<ApiResponse<ChequeSettingsPublic>> => {
    return request<ChequeSettingsPublic>(`/organizations/cheque-settings/${id}`);
  },

  create: async (data: CreateChequeSettingsRequest): Promise<ApiResponse<ChequeSettingsPublic>> => {
    return request<ChequeSettingsPublic>('/organizations/cheque-settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (
    id: string,
    data: UpdateChequeSettingsRequest,
  ): Promise<ApiResponse<ChequeSettingsPublic>> => {
    return request<ChequeSettingsPublic>(`/organizations/cheque-settings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    return request<null>(`/organizations/cheque-settings/${id}`, {
      method: 'DELETE',
    });
  },

  getCurrentChequeNumber: async (
    id: string,
  ): Promise<ApiResponse<{ currentChequeNumber: number }>> => {
    return request<{ currentChequeNumber: number }>(
      `/organizations/cheque-settings/${id}/cheque-number`,
    );
  },

  getNextChequeNumber: async (id: string): Promise<ApiResponse<{ nextChequeNumber: number }>> => {
    return request<{ nextChequeNumber: number }>(
      `/organizations/cheque-settings/${id}/next-cheque-number`,
      { method: 'POST' },
    );
  },
};

// Export all APIs
export const organizationSettingsApi = {
  companyInfo: companyInfoApi,
  bankAccounts: bankAccountsApi,
  signatures: signaturesApi,
  chequeSettings: chequeSettingsApi,
};
